/* 3rd Sense Aromatica — cart stand-included bar + one-tap add / upgrade.
 * Hooks into Horizon's own CartLinesUpdateEvent so the drawer, cart page and
 * header bubble all re-render through the theme's normal path.
 * (Opening the cart drawer after "Add to cart" uses the theme's own auto_open_cart_drawer setting.) */
import { CartLinesUpdateEvent } from '@shopify/events';

const rate = () => parseFloat(window.Shopify?.currency?.rate || '1') || 1;
const currency = (el) => window.Shopify?.currency?.active || el.dataset.shopCurrency || 'USD';
const fmt = (cents, cur) =>
  new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: cur,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);

const STAND_KEY = 'ts-stand-auto-added';
const ssGet = (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } };
const ssSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) {} };

/* Stand bar: Liquid renders correct text for the shop currency;
 * when Markets presents another currency, rescale the threshold here.
 * Also auto-adds the included stand (as a $0 line via the automatic discount) the first time it qualifies,
 * so it shows on the order, packing slip and invoice. If the shopper removes it, we don't re-add it
 * this session — the "Add stand" button stays available. */
class ThreeSenseRewards extends HTMLElement {
  static observedAttributes = ['data-qualifying', 'class'];
  attributeChangedCallback() {
    if (this.isConnected) queueMicrotask(() => this.connectedCallback());
  }
  connectedCallback() {
    this.#rescale();
    clearTimeout(this._autoTimer);
    this._autoTimer = setTimeout(() => this.#autoClaim(), 400);
  }

  #autoClaim() {
    if (!this.isConnected || !this.classList.contains('is-gift')) return;
    if (ssGet(STAND_KEY)) return;
    const btn = this.querySelector('.ts-gift__cta button');
    if (!btn || this.querySelector('.ts-gift__cta[aria-busy="true"]')) return;
    ssSet(STAND_KEY, '1');
    btn.click();
  }

  #rescale() {
    const r = rate();
    if (r === 1 || !this.dataset.gift) return;
    const cur = currency(this);
    const q = +this.dataset.qualifying;
    const gift = Math.round(+this.dataset.gift * r);
    const ok = q >= gift;

    this.classList.toggle('is-gift', ok);
    const fill = this.querySelector('[data-fill]');
    if (fill) fill.style.setProperty('--ts-fill', `${Math.min(100, (q / gift) * 100)}%`);
    this.querySelectorAll('[data-gift-label]').forEach((el) => (el.textContent = fmt(gift, cur)));

    const msg = this.querySelector('[data-msg]');
    if (!msg) return;
    msg.innerHTML = ok
      ? '<span class="ts-spark">✦</span> Wooden incense stand included with your order'
      : `Add <strong>${fmt(gift - q, cur)}</strong> more and we'll include a <strong>wooden incense stand</strong>`;
  }
}

const postJSON = (url, body) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

/* One-tap add for the stand and upsell rail.
 * data-replace-key: gift-set upgrade. Adds the new set FIRST, then removes the old line,
 * so a failed add never leaves the shopper with an emptier cart. */
class ThreeSenseAdd extends HTMLElement {
  connectedCallback() {
    // Delegate on the host so the listener survives section morphs that swap the inner button.
    this.addEventListener('click', this.#onClick);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }
  #onClick = (e) => {
    if (e.target instanceof Element && e.target.closest('button')) this.#add();
  };

  #add = async () => {
    if (this.getAttribute('aria-busy') === 'true') return;
    this.setAttribute('aria-busy', 'true');
    const id = Number(this.dataset.variantId);
    const replaceKey = this.dataset.replaceKey;
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const sectionParams = { sections: sectionIds.join(','), sections_url: window.location.pathname };

    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (e) {
      deferred = null;
    }

    try {
      const res = await postJSON(`${root}cart/add.js`, {
        items: [{ id, quantity: 1 }],
        ...(replaceKey ? {} : sectionParams),
      });
      let data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Add failed');
      const added = data.items;

      if (replaceKey) {
        const rm = await postJSON(`${root}cart/change.js`, { id: replaceKey, quantity: 0, ...sectionParams });
        data = await rm.json();
        if (!rm.ok || data.status) throw new Error(data.description || 'Could not remove the old set');
      }

      if (!deferred) return window.location.reload();
      const cartRes = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse?.(cart) ?? cart,
        detail: {
          sections: data.sections,
          items: added,
          itemCount: cart.item_count,
          source: 'three-sense-add',
          didError: false,
        },
      });
      this.removeAttribute('aria-busy');
    } catch (err) {
      console.warn('[3s-cart]', err);
      deferred?.reject?.(err);
      this.removeAttribute('aria-busy');
      if (replaceKey) return window.location.reload();
      const btn = this.querySelector('button');
      if (btn) {
        const label = btn.textContent;
        btn.textContent = 'Unavailable';
        setTimeout(() => (btn.textContent = label), 2000);
      }
    }
  };
}

if (!customElements.get('three-sense-rewards')) customElements.define('three-sense-rewards', ThreeSenseRewards);
if (!customElements.get('three-sense-add')) customElements.define('three-sense-add', ThreeSenseAdd);